import { DestroyRef, Injectable, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { GoogleIdentityService } from 'src/app/services/identity.service';
import { AdvancedDebugging } from 'src/app/utilities';

const OP_INIT = 'dashboard:DashboardAppShellFacade.init';
const OP_NAVIGATE = 'dashboard:DashboardAppShellFacade.navigate';

/** Dashboard root: auth-driven route to dashboard vs sign-in; scoped to {@link AppComponent} via `providers`. */
@Injectable()
export class DashboardAppShellFacade {
	private readonly identityService = inject(GoogleIdentityService);
	private readonly router = inject(Router);
	private readonly destroyRef = inject(DestroyRef);
	private readonly advancedDebugging = new AdvancedDebugging();

	constructor() {
		this.identityService.getUser.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
			next: user => {
				const route = this.resolveAuthRoute(!!user?.email);
				if (!route) return;
				void this.router.navigate([route]).catch(error =>
					this.advancedDebugging.captureError(OP_NAVIGATE, error, {
						tags: { operation: OP_NAVIGATE, app: 'dashboard' },
						route,
						url: this.router.url,
					}),
				);
			},
			error: error =>
				this.advancedDebugging.captureError(OP_INIT, error, {
					tags: { operation: OP_INIT, app: 'dashboard' },
				}),
		});
	}

	/**
	 * Only move between sign-in and the home list.
	 * Do not force `dashboard` on every auth emission — that hijacks `/parents/:id` and `/yas/:id`.
	 */
	private resolveAuthRoute(signedIn: boolean): 'dashboard' | 'sign-in' | null {
		const url = this.router.url.split('?')[0] || '/';
		const onSignIn = url.startsWith('/sign-in');

		if (!signedIn) {
			return onSignIn ? null : 'sign-in';
		}
		return onSignIn ? 'dashboard' : null;
	}
}
